import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSocialMeta } from '../utils/seo';
import { FileText } from 'lucide-react';

const TermsOfService = () => {
  useEffect(() => {
    document.title = 'Terms of Service - Pin High Golf & Lounge';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Terms of Service for Pin High Golf & Lounge. Read our terms and conditions for using our indoor golf simulator facilities and services.');
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/terms');
    updateSocialMeta({
      title: 'Terms of Service - Pin High Golf & Lounge',
      description: 'Terms of Service for Pin High Golf & Lounge. Read our terms and conditions for using our indoor golf simulator facilities and services.',
      path: '/terms',
    });

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms of Service',
      url: 'https://pinhighbay.com/terms',
      isPartOf: { '@id': 'https://pinhighbay.com/#website' },
      dateModified: '2025-03-15',
    };
    let wpScript = document.querySelector('#terms-webpage-schema');
    if (!wpScript) {
      wpScript = document.createElement('script');
      wpScript.id = 'terms-webpage-schema';
      wpScript.type = 'application/ld+json';
      document.head.appendChild(wpScript);
    }
    wpScript.textContent = JSON.stringify(webPageSchema);

    return () => {
      const el = document.querySelector('#terms-webpage-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 to-green-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-green-300" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-lg text-green-50">Last updated: March 2025</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
          <p className="text-gray-600 mb-6">
            By accessing or using the services of Pin High Golf & Lounge ("Company," "we," "us," or "our"), located at 27229 Wolf Road, Bay Village, OH 44140, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Services</h2>
          <p className="text-gray-600 mb-6">
            Pin High Golf & Lounge provides indoor golf simulator experiences, lessons, league play, event hosting, food and beverage services, and related amenities. Our services are subject to availability and may be modified at any time.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Reservations and Payments</h2>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li><strong>Reservations:</strong> Bay reservations are recommended and can be made online through our booking system or by phone.</li>
            <li><strong>Payment:</strong> Full payment or a valid credit card is required to secure reservations. We accept major credit cards and cash.</li>
            <li><strong>Cancellation Policy:</strong> Cancellations made more than 24 hours before your reservation will receive a full refund. Cancellations within 24 hours may be subject to a cancellation fee.</li>
            <li><strong>No-shows:</strong> Failure to arrive within 15 minutes of your scheduled time may result in cancellation of your reservation and forfeiture of any prepaid fees.</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Facility Rules</h2>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li><strong>Age Requirements:</strong> Guests must be 21 years or older with valid ID to purchase or consume alcohol.</li>
            <li><strong>Dress Code:</strong> Casual attire is acceptable. No metal spikes or cleats are permitted on simulator mats.</li>
            <li><strong>Equipment:</strong> Guests may use their own golf clubs or borrow from our available selection. All equipment must be used responsibly.</li>
            <li><strong>Conduct:</strong> We reserve the right to refuse service or remove any guest engaging in disruptive, unsafe, or inappropriate behavior.</li>
            <li><strong>No Outside Food or Beverages:</strong> Outside food and alcoholic beverages are not permitted.</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Liability and Assumption of Risk</h2>
          <p className="text-gray-600 mb-6">
            Golf simulator activities involve physical activity and inherent risks. By using our facilities, you acknowledge and assume these risks. Pin High Golf & Lounge is not liable for any injuries, accidents, or damage to personal property that may occur during your visit, except in cases of gross negligence.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Damage to Property</h2>
          <p className="text-gray-600 mb-6">
            Guests are responsible for any damage to facility equipment or property caused by negligent or intentional actions. Repair or replacement costs may be charged to the guest's payment method on file.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Intellectual Property</h2>
          <p className="text-gray-600 mb-6">
            All content on our website, including text, graphics, logos, and images, is the property of Pin High Golf & Lounge and is protected by copyright laws. You may not reproduce, distribute, or use our content without written permission.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Privacy</h2>
          <p className="text-gray-600 mb-6">
            Your use of our services is also governed by our <Link to="/privacy" className="text-green-600 hover:text-green-700">Privacy Policy</Link>, which is incorporated into these Terms by reference.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Memberships</h2>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li>Memberships are non-transferable and for individual use only unless otherwise specified.</li>
            <li>Membership benefits are subject to change with reasonable notice.</li>
            <li>Monthly memberships may be cancelled with 30 days written notice.</li>
            <li>Annual memberships are non-refundable after the 30-day trial period.</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Limitation of Liability</h2>
          <p className="text-gray-600 mb-6">
            To the maximum extent permitted by law, Pin High Golf & Lounge shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Governing Law</h2>
          <p className="text-gray-600 mb-6">
            These Terms shall be governed by and construed in accordance with the laws of the State of Ohio, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of Cuyahoga County, Ohio.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Changes to Terms</h2>
          <p className="text-gray-600 mb-6">
            We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after any changes constitutes acceptance of the new Terms.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Contact Information</h2>
          <p className="text-gray-600 mb-4">For questions about these Terms, please contact us:</p>
          <div className="bg-gray-50 p-6 rounded-lg text-gray-600">
            <p className="font-semibold text-gray-900">Pin High Golf & Lounge</p>
            <p>27229 Wolf Road</p>
            <p>Bay Village, OH 44140</p>
            <p>Phone: (440) 455-1078</p>
            <p>Email: dennis@pinhighbay.com</p>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <Link to="/" className="text-green-600 hover:text-green-700 font-medium">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
